import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { buildNbaAllTimeGreatsPath } from "../../constants/nbaAllTimeGreats";
import { serializeHeadshotIds } from "../../utils/playerHeadshots";
import { buildNbaSimilarSeasonsPath } from "../../components/similarSeasons/similarSeasonsQuery";

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8008/api";
  }
  return "/api";
};

const mapBioData = (player) => {
  if (!player) return player;
  if (player.bioData) {
    return { ...player, bio: player.bioData };
  }
  return player;
};

const mapPlayers = (players = []) => players.map(mapBioData);

export const nbaApi = createApi({
  reducerPath: "nbaApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      headers.set("Cache-Control", "no-cache");

      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return headers;
    },
    responseHandler: async (response) => {
      const data = await response.json();

      if (response.status === 403 && data.code === "ADMIN_REQUIRED") {
        window.location.href = "/app";
      }

      return data;
    },
  }),
  tagTypes: ["NbaPlayer", "NbaLeaderboard"],
  endpoints: (builder) => ({
    getNbaPlayerById: builder.query({
      query: (id) => `/nba/players/${id}`,
      transformResponse: mapBioData,
      providesTags: (result, error, id) => [{ type: "NbaPlayer", id }],
    }),

    searchNbaPlayers: builder.query({
      query: (searchTerm) =>
        `/nba/players/search?name=${encodeURIComponent(searchTerm)}`,
      transformResponse: (response) => mapPlayers(response.results || []),
      providesTags: ["NbaPlayer"],
    }),

    getNbaPositionLeaders: builder.query({
      query: (slot) =>
        `/nba/players/position-leaders/${encodeURIComponent(slot)}`,
      providesTags: (_result, _error, slot) => [
        { type: "NbaPlayer", id: `position-leaders-${slot}` },
      ],
    }),

    autocompleteNbaPlayers: builder.query({
      query: (q) =>
        `/nba/players/autocomplete?q=${encodeURIComponent(q)}`,
      transformResponse: (response) => mapPlayers(response),
      providesTags: ["NbaPlayer"],
    }),

    getNbaTopPlayers: builder.query({
      query: (category = "points") => `/nba/top-players/${category}`,
      transformResponse: (response) => ({
        ...response,
        players: mapPlayers(response.players || []),
      }),
      providesTags: ["NbaLeaderboard"],
    }),

    getNbaAllTopPlayers: builder.query({
      query: () => "/nba/top-players",
      providesTags: ["NbaLeaderboard"],
    }),

    getNbaAllTimeGreatsLeaders: builder.query({
      query: ({ stat, page = 1, pageSize = 25 } = {}) =>
        buildNbaAllTimeGreatsPath({ stat, page, pageSize }),
      providesTags: ["NbaLeaderboard"],
    }),

    getNbaSimilarSeasons: builder.query({
      query: ({ playerId, season, limit = 8, excludeSamePlayer = true, model } = {}) =>
        buildNbaSimilarSeasonsPath({
          playerId,
          season,
          limit,
          excludeSamePlayer,
          model,
        }),
    }),

    getNbaPlayerHeadshots: builder.query({
      query: (ids) =>
        `/nba/players/headshots?ids=${serializeHeadshotIds(ids)}`,
      transformResponse: (response) => response.headshots || {},
      keepUnusedDataFor: 86400,
    }),

    getNbaTeamPlayers: builder.query({
      query: (franchiseId) => `/nba/teams/${encodeURIComponent(franchiseId)}`,
      providesTags: (result, error, franchiseId) => [
        { type: "NbaPlayer", id: `team-${franchiseId}` },
      ],
    }),
  }),
});

export const {
  useGetNbaPlayerByIdQuery,
  useSearchNbaPlayersQuery,
  useGetNbaPositionLeadersQuery,
  useAutocompleteNbaPlayersQuery,
  useGetNbaTopPlayersQuery,
  useGetNbaAllTopPlayersQuery,
  useGetNbaAllTimeGreatsLeadersQuery,
  useGetNbaSimilarSeasonsQuery,
  useGetNbaPlayerHeadshotsQuery,
  useGetNbaTeamPlayersQuery,
} = nbaApi;
